/* Finding a customer when the six digits cannot.
 *
 * WHY THIS SCREEN EXISTS, AND IT IS TWO REASONS THAT LOOK ALIKE.
 *
 * The first is the one a cashier meets weekly: a flat battery, a phone left in
 * a bag, a basement cafe with no signal. The endpoint for it has existed and
 * been authorised for weeks with nothing calling it, which is the quietest way
 * for a feature to not exist.
 *
 * The second is the ambiguous code. Six digits is a 10^6 space and two people
 * can hold the same one in the same window. The server used to answer that
 * with masked labels for a one-tap choice -- `Q•••• 1. •• 6718` -- and both
 * halves were wrong. Nobody reads that under queue pressure; the cashier taps
 * the top one and stamps a stranger, which is what the picker was for. And one
 * of the two is an uninvolved person whose partial name and last four digits
 * were being handed to a merchant's employee for no processing purpose at all.
 *
 * So the server now says how many matched and nothing about who, and this is
 * where the counter goes instead. The cashier finds the person they are
 * actually looking at, and the SAME CODE still does the stamping -- the server
 * validates `chosen_user_id` against that attempt's own candidate set, so this
 * narrows an identity rather than asserting one.
 *
 * WHAT THIS SCREEN CANNOT DO, stated on it rather than discovered. The search
 * is scoped to this merchant's own members, which is a privacy boundary and
 * not an optimisation: a global search would let anyone with a till type a
 * phone number and learn whether it belongs to a Damga customer. The cost is
 * that a first-time customer with a dead phone cannot be enrolled here. They
 * join next visit, with their code.
 *
 * And it cannot stamp a RETURNING customer who has no code at all -- a flat
 * battery on somebody who is already a member. That is the manager override,
 * which has its endpoint, its PIN, its cap and its reason codes on the server
 * and no screen yet. A customer who is not a member is a different case and
 * has one: "nobody here matches that" now offers enrolment.
 */
import { Button } from "@damga/ds";
import { t } from "@damga/kit";
import { useCallback, useEffect, useRef, useState } from "react";

export interface Match {
  user_id: string;
  member_id: string;
  label: string;
  balance: number;
  /** The member's own pinned threshold. Null only if their programme has none. */
  threshold: number | null;
}

interface Props {
  /** Runs the search. Rejects like any other call; `Offline` is handled here. */
  search(q: string): Promise<Match[]>;
  /** Stamp this person. Carries the pending code when there is one. */
  onPick(userId: string): void;
  onBack(): void;
  /** The way out of "nobody here matches that", carrying what was typed. */
  onEnrol(phone: string): void;
  online: boolean;
  busy: boolean;
  /** Set when we arrived here from an ambiguous code rather than from the pad. */
  ambiguous?: boolean;
}

export function Lookup({ search, onPick, onBack, onEnrol, online, busy, ambiguous }: Props) {
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState<Match[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);
  const field = useRef<HTMLInputElement>(null);

  // The cashier is mid-transaction with someone in front of them. Focus goes
  // to the field, not to the first button, so typing starts immediately.
  useEffect(() => {
    field.current?.focus();
  }, []);

  const run = useCallback(async () => {
    const term = q.trim();
    if (term.length < 2 || searching) return;
    setSearching(true);
    setFailed(false);
    try {
      setMatches(await search(term));
    } catch {
      // Not distinguished by cause on purpose. Offline and a 500 read the same
      // way to the person holding the tablet, and the useful sentence is the
      // same in both: the keypad still works.
      setFailed(true);
      setMatches(null);
    } finally {
      setSearching(false);
    }
  }, [q, search, searching]);

  return (
    <section className="lookup">
      {ambiguous ? (
        <div className="lookup-why">
          <p className="till-panel-title">{t("ambiguous.title")}</p>
          <p className="muted">{t("ambiguous.body")}</p>
        </div>
      ) : (
        <p className="label">{t("lookup.title")}</p>
      )}

      <form
        className="lookup-form"
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
      >
        <input
          ref={field}
          className="till-input"
          type="search"
          inputMode="search"
          autoComplete="off"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("lookup.placeholder")}
          aria-label={t("lookup.placeholder")}
          disabled={busy}
        />
        <Button
          size="counter"
          variant="primary"
          type="submit"
          disabled={busy || searching || q.trim().length < 2 || !online}
        >
          {searching ? t("lookup.searching") : t("lookup.search")}
        </Button>
      </form>

      <p className="muted lookup-hint">{online ? t("lookup.hint") : t("lookup.offline")}</p>

      {failed && <p className="till-error">{t("lookup.offline")}</p>}

      {matches !== null && matches.length === 0 && !failed && (
        <>
          <p className="muted">{t("lookup.none")}</p>
          {/* THE DEAD END, ENDED. Until this existed a cashier who searched for
              a first-time customer was told nobody matched and had nowhere to
              go -- which is every new customer, which is the whole acquisition
              path. */}
          <Button size="counter" variant="primary" onClick={() => onEnrol(q.trim())} fullWidth>
            {t("enrol.submit")}
          </Button>
        </>
      )}

      {matches !== null && matches.length > 0 && (
        <ul className="lookup-results">
          {matches.map((m) => (
            <li key={m.user_id}>
              <button
                className="lookup-hit"
                onClick={() => onPick(m.user_id)}
                disabled={busy}
                type="button"
              >
                <span className="lookup-who">{m.label}</span>
                <span className="lookup-card mono">
                  {m.threshold
                    ? t("lookup.card", "en", {
                        balance: String(m.balance),
                        threshold: String(m.threshold),
                      })
                    : t("lookup.noCard")}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Always visible, including before a search. It is the sentence that
          stops a cashier hunting for a first-time customer who is not here to
          be found. */}
      <p className="muted lookup-scope">{t("lookup.scope")}</p>

      <Button size="counter" variant="ghost" onClick={onBack} fullWidth>
        {t("lookup.back")}
      </Button>
    </section>
  );
}
